/**
 * Pressure modulation for brush line-width.
 *
 * M2.T3 per IMPLEMENTATION_PLAN §7 M2.T3 + PRD §6.2.
 *
 * Rules (per PRD):
 *   - `pointerType === 'pen'` AND `pressure > 0` → line width = selectedWidth × pressure_normalized.
 *   - Mouse / finger → constant width = selectedWidth.
 *
 * `pressure_normalized` is clamped to [MIN_PRESSURE_FACTOR, 1.0] so very light
 * pen touches stay visible (the raw PointerEvent.pressure range is [0,1]).
 *
 * The minimum factor prevents strokes from becoming invisible at the lightest
 * pressure; 0.2 is a sensible floor that matches typical drawing-app behaviour.
 */

/** Minimum pressure scale factor (pen touch never drops below 20% of base width). */
export const MIN_PRESSURE_FACTOR = 0.2;

/**
 * Returns the effective brush line-width given:
 *  @param baseWidth: the selected brush size in logical pixels
 *  @param pointerType: from PointerEvent.pointerType
 *  @param pressure: from PointerEvent.pressure [0, 1]
 */
export function effectiveBrushWidth(
  baseWidth: number,
  pointerType: string,
  pressure: number,
): number {
  if (pointerType === "pen" && pressure > 0) {
    const factor = Math.max(MIN_PRESSURE_FACTOR, Math.min(1, pressure));
    return baseWidth * factor;
  }
  // Mouse and touch use constant width.
  return baseWidth;
}
